import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const isRootDomain = parts.length === 2 || (parts.length === 3 && parts[0] === 'www');
  const currentSubdomain = !isRootDomain && parts.length >= 3 ? parts[0] : null;

  useEffect(() => { if (currentSubdomain) setSubdomain(currentSubdomain); }, [currentSubdomain]);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Impersonation: platform admin opens a tenant workspace using a short-lived token.
  useEffect(() => {
    if (isRootDomain) return;
    const params = new URLSearchParams(window.location.search);
    const tok = params.get('_tok');
    if (!tok) return;
    // Store the token exactly as a normal auth token and redirect to POS.
    localStorage.setItem('auth_token', tok);
    // Clean the URL then navigate so the token isn't visible in history.
    window.history.replaceState({}, '', '/pos');
    navigate('/pos', { replace: true });
  }, []);

  const handleSubdomainRedirect = (e: React.FormEvent) => {
    e.preventDefault();
    if (!subdomain.trim()) { setError('Please enter your organization subdomain'); return; }
    window.location.href = `${window.location.protocol}//${subdomain.toLowerCase()}.taplo.cc/login`;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, subdomain || undefined);
      navigate('/pos');
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = isRootDomain ? handleSubdomainRedirect : handleLogin;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f0f4f8] font-sans p-6 selection:bg-blue-200">
      <div className="bg-white p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 max-w-[440px] w-full border border-slate-200/60 transition-all">

        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <img src="/taplo-wordmark.png" alt="Taplo" className="h-12 md:h-14 mb-3 select-none" draggable={false} />
          <p className="mt-2 text-[15px] font-medium text-slate-500 text-center">
            {isRootDomain
              ? 'Enter your organization to continue'
              : `Sign in to ${currentSubdomain}`
            }
          </p>
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 text-sm font-medium flex items-center justify-between">
            {error}
            <button type="button" onClick={() => setError('')} className="text-red-400 hover:text-red-700 text-xl leading-none font-bold opacity-70 hover:opacity-100">&times;</button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {isRootDomain ? (
            <div>
              <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Organization</label>
              <div className="flex items-stretch h-14 relative group">
                <input
                  type="text"
                  placeholder="your-org"
                  value={subdomain}
                  onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  required
                  autoFocus
                  className="flex-1 px-5 bg-white border border-slate-200 border-r-0 rounded-l-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                />
                <span className="px-5 flex items-center bg-slate-50 border border-slate-200 border-l-0 rounded-r-2xl text-[15px] font-medium text-slate-400 whitespace-nowrap group-focus-within:border-[#2563eb] transition-colors">
                  .taplo.cc
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Email</label>
                <input className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300" type="email" placeholder="jane@example.com" value={email} onChange={e => setEmail(e.target.value)} required />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Password</label>
                <input className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`
              w-full h-14 mt-3 rounded-2xl flex items-center justify-center text-[16px] font-bold transition-all
              ${loading
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-[#2563eb] text-white shadow-lg shadow-blue-900/20 hover:bg-[#1d4ed8] active:scale-[0.98]'
              }
            `}
          >
            {loading ? 'Please wait…' : isRootDomain ? 'Continue' : 'Sign In'}
          </button>
        </form>

        {!isRootDomain && (
          <p className="mt-8 text-center text-[15px] font-medium text-slate-500">
            Need an account? Ask your administrator for an invite link.
          </p>
        )}

        {isRootDomain && (
          <p className="mt-8 text-center text-[15px] font-medium text-slate-500">
            Don't have an organization? Registration is by invitation only — contact your administrator.
          </p>
        )}
      </div>
    </div>
  );
}
